import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { X, Plus, Trash2, Loader2, MessageSquare } from "lucide-react";

import api from "../../services/api";
import toastError from "../../errors/toastError";

const BotStepModal = ({ open, onClose, step, flowId, onSave }) => {
  const initialState = {
    stepOrder: 1,
    message: "",
    stepType: "message",
    options: [],
    waitForResponse: false,
    mediaUrl: "",
    mediaType: "",
  };

  const [form, setForm] = useState(initialState);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (step) {
      setForm({
        stepOrder: step.stepOrder || 1,
        message: step.message || "",
        stepType: step.stepType || "message",
        options: step.options || [],
        waitForResponse: step.waitForResponse || false,
        mediaUrl: step.mediaUrl || "",
        mediaType: step.mediaType || "",
      });
    } else {
      setForm(initialState);
    }
  }, [step, open]);

  const handleClose = () => {
    setForm(initialState);
    onClose();
  };

  const handleSave = async () => {
    if (!form.message && form.stepType !== "options" && form.stepType !== "end") {
      toast.warn("Escribe un mensaje");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, flowId };
      if (step) {
        await api.put(`/bot/steps/${step.id}`, payload);
        toast.success("Paso actualizado");
      } else {
        await api.post("/bot/steps", payload);
        toast.success("Paso creado");
      }
      if (onSave) onSave();
      handleClose();
    } catch (err) { toastError(err); }
    setSaving(false);
  };

  const addOption = () => {
    setForm(prev => ({
      ...prev,
      options: [...prev.options, { id: `opt_${Date.now()}`, text: "", nextStepId: null }]
    }));
  };

  const updateOption = (idx, field, value) => {
    setForm(prev => {
      const opts = [...prev.options];
      opts[idx] = { ...opts[idx], [field]: value };
      return { ...prev, options: opts };
    });
  };

  const removeOption = (idx) => {
    setForm(prev => ({
      ...prev,
      options: prev.options.filter((_, i) => i !== idx)
    }));
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={handleClose} />
      <div className="relative bg-white dark:bg-slate-900 rounded-xl shadow-xl border border-slate-200 dark:border-slate-700 w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 dark:border-slate-700">
          <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100 flex items-center gap-2">
            <MessageSquare className="w-4 h-4 text-indigo-600" />
            {step ? `Editar Paso (ID: ${step.id})` : "Nuevo Paso"}
          </h2>
          <button onClick={handleClose} className="p-1 rounded-lg text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-5 py-4 space-y-4 bg-slate-50 dark:bg-slate-800/30">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-2">Orden</label>
              <input
                type="number"
                min="1"
                value={form.stepOrder}
                onChange={(e) => setForm(prev => ({ ...prev, stepOrder: parseInt(e.target.value) || 1 }))}
                className="w-full px-3 py-2.5 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100"
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-2">Tipo</label>
              <select
                value={form.stepType}
                onChange={(e) => setForm(prev => ({ ...prev, stepType: e.target.value }))}
                className="w-full px-3 py-2.5 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100"
              >
                <option value="message">Mensaje</option>
                <option value="question">Pregunta</option>
                <option value="options">Opciones</option>
                <option value="end">Fin</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-2">Mensaje</label>
            <textarea
              value={form.message}
              onChange={(e) => setForm(prev => ({ ...prev, message: e.target.value }))}
              rows={4}
              className="w-full px-3 py-2.5 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 resize-none"
              placeholder={form.stepType === "options" ? "Elige una opción:" : "Escribe el mensaje que verá el usuario..."}
            />
          </div>

          {form.stepType === "options" && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Opciones</label>
                <button
                  type="button"
                  onClick={addOption}
                  className="flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-700 font-medium"
                >
                  <Plus className="w-3 h-3" /> Agregar
                </button>
              </div>
              <div className="space-y-2">
                {form.options.map((opt, idx) => (
                  <div key={opt.id} className="flex items-center gap-2">
                    <span className="w-6 h-6 flex items-center justify-center bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 rounded-full text-xs font-bold">
                      {idx + 1}
                    </span>
                    <input
                      type="text"
                      value={opt.text}
                      onChange={(e) => updateOption(idx, "text", e.target.value)}
                      placeholder="Texto de la opción"
                      className="flex-1 px-3 py-2 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100"
                    />
                    <input
                      type="number"
                      value={opt.nextStepId || ""}
                      onChange={(e) => updateOption(idx, "nextStepId", e.target.value ? parseInt(e.target.value) : null)}
                      placeholder="ID"
                      title={`ID del paso destino (dejar vacío para ir al siguiente paso en orden)`}
                      className="w-20 px-2 py-2 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100"
                    />
                    <button
                      type="button"
                      onClick={() => removeOption(idx)}
                      className="p-1 text-red-500 hover:bg-red-50 dark:hover:bg-red-950/30 rounded-lg transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
                {form.options.length === 0 && (
                  <p className="text-xs text-slate-400 text-center py-2">Sin opciones. Agrega al menos una.</p>
                )}
                {form.options.length > 0 && (
                  <p className="text-xs text-slate-400 mt-2">💡 En "ID" indica el ID del paso destino (ver ID en la lista de pasos). Si lo dejas vacío, irá al siguiente paso en orden.</p>
                )}
              </div>
            </div>
          )}

          {(form.stepType === "question" || form.stepType === "options") && (
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={form.waitForResponse}
                onChange={(e) => setForm(prev => ({ ...prev, waitForResponse: e.target.checked }))}
                className="rounded"
              />
              <span className="text-sm text-slate-700 dark:text-slate-300">Esperar respuesta del usuario</span>
            </label>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-slate-200 dark:border-slate-700">
          <button type="button" onClick={handleClose} disabled={saving} className="px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-slate-700 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors">
            Cancelar
          </button>
          <button onClick={handleSave} disabled={saving} className="flex items-center gap-1.5 px-4 py-2 text-sm font-semibold bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors">
            {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            {step ? "Guardar" : "Crear"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BotStepModal;
